import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Container,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import {
  CilPointsHero,
  CilPointsSection,
  CilPointsStepCard,
  CIL_POINTS_LANDING_ACCENTS,
} from '../components/CILPoints/CilPointsLandingLayout';
import {
  disclaimer,
  pageCtas,
  pageFaq,
  pageHero,
  pageSections,
} from '../content/cilPointsPublicContent';
import { CIL_POINTS_LABEL } from '../utils/cilPoints';

const { ACCENT, SECONDARY } = CIL_POINTS_LANDING_ACCENTS;

const stepCards = [
  {
    step: 'Ottieni',
    title: 'Partecipa agli eventi',
    description:
      "Tornei e iniziative CIL selezionate possono prevedere l'assegnazione di punti secondo le regole comunicate.",
    accent: ACCENT,
  },
  {
    step: 'Accumula',
    title: 'Saldo sul tuo account',
    description:
      'I punti accredati restano nel tuo profilo: li vedi in area personale e li usi quando vuoi.',
    accent: SECONDARY,
  },
  {
    step: 'Usa',
    title: 'Premi ed eventi',
    description:
      'Catalogo premi con ritiro in sede, oppure pagamento punti sulle prenotazioni dove previsto.',
    accent: ACCENT,
  },
];

function CilPoints() {
  return (
    <Box bg="#36393f" minH="100vh" overflowX="hidden" color="white">
      <Helmet>
        <title>{CIL_POINTS_LABEL} | CIL – Creatività in Libertà</title>
        <meta
          name="description"
          content={`Scopri cosa sono i ${CIL_POINTS_LABEL}, come ottenerli partecipando agli eventi CIL e come usarli per premi e iniziative associative.`}
        />
      </Helmet>

      <CilPointsHero
        badge={pageHero.badge}
        title={pageHero.title}
        subtitle={pageHero.subtitle}
      />

      <Container maxW="7xl" py={{ base: 12, md: 20 }} px={{ base: 4, md: 6 }}>
        <VStack spacing={{ base: 14, md: 20 }}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
            {stepCards.map((card) => (
              <CilPointsStepCard key={card.step} {...card} />
            ))}
          </SimpleGrid>

          {pageSections.map((section) => (
            <CilPointsSection
              key={section.id}
              id={section.id}
              badge={section.badge}
              title={section.title}
            >
              <VStack align="stretch" spacing={3}>
                {section.paragraphs.map((p) => (
                  <Text key={p.slice(0, 40)} color="gray.300" lineHeight="tall">
                    {p}
                  </Text>
                ))}
                {section.bullets?.length > 0 && (
                  <List spacing={2} color="gray.300" fontSize="md">
                    {section.bullets.map((item) => (
                      <ListItem
                        key={item}
                        display="flex"
                        alignItems="flex-start"
                      >
                        <ListIcon
                          as={CheckCircleIcon}
                          color={ACCENT}
                          mt={1}
                          flexShrink={0}
                        />
                        {item}
                      </ListItem>
                    ))}
                  </List>
                )}
                {section.cta && (
                  <Button
                    as={RouterLink}
                    to={section.cta.to}
                    size="lg"
                    mt={2}
                    bg={ACCENT}
                    color="black"
                    border="2px solid"
                    borderColor={ACCENT}
                    fontWeight="600"
                    borderRadius="12px"
                    _hover={{
                      transform: 'translateY(-2px)',
                      bg: '#e6b800',
                    }}
                    transition="all 0.2s ease"
                  >
                    {section.cta.label}
                  </Button>
                )}
              </VStack>
            </CilPointsSection>
          ))}

          <Box
            w="full"
            maxW="3xl"
            mx="auto"
            p={6}
            bg="#2f3136"
            borderRadius="16px"
            borderWidth="2px"
            borderColor={SECONDARY}
          >
            <Text fontWeight="bold" fontSize="lg" color="white" mb={3}>
              {disclaimer.title}
            </Text>
            <Text color="gray.300" lineHeight="tall">
              {disclaimer.body}
            </Text>
          </Box>

          <CilPointsSection badge="FAQ" title="Domande frequenti">
            <Accordion allowToggle w="full" defaultIndex={[0]}>
              {pageFaq.map((item, index) => (
                <AccordionItem
                  key={item.q}
                  border="1px solid"
                  borderColor="gray.600"
                  borderRadius="lg"
                  mb={index < pageFaq.length - 1 ? 2 : 0}
                  overflow="hidden"
                  bg="#2f3136"
                >
                  <AccordionButton py={4} _expanded={{ bg: '#40444b' }}>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      {item.q}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} color="gray.300" lineHeight="tall">
                    {item.a}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </CilPointsSection>

          <VStack spacing={4} w="full" pt={4}>
            <Text fontSize="lg" fontWeight="bold" color="white">
              Pronto per iniziare?
            </Text>
            <SimpleGrid
              columns={{ base: 1, sm: 2, md: 4 }}
              spacing={4}
              w="full"
              maxW="4xl"
            >
              {pageCtas.map((cta) => (
                <Button
                  key={cta.to}
                  as={RouterLink}
                  to={cta.to}
                  size="lg"
                  bg={cta.primary ? ACCENT : '#2f3136'}
                  color={cta.primary ? 'black' : 'white'}
                  border="2px solid"
                  borderColor={cta.primary ? ACCENT : SECONDARY}
                  fontWeight="600"
                  borderRadius="12px"
                  _hover={{
                    transform: 'translateY(-2px)',
                    bg: cta.primary ? '#e6b800' : '#36393f',
                  }}
                  transition="all 0.2s ease"
                >
                  {cta.label}
                </Button>
              ))}
            </SimpleGrid>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
}

export default CilPoints;
